import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

function OptionCard({ label, selected, onClick }) {
  return (
    <motion.button
      variants={fadeUp}
      onClick={onClick}
      aria-pressed={selected}
      data-testid="assessment-option-card"
      className={`w-full min-h-[56px] text-left rounded-2xl border-2 px-5 py-4 flex items-center justify-between gap-3 transition-all duration-300 ${
        selected
          ? "border-gold bg-gold-light shadow-[0_0_0_4px_rgba(197,155,39,0.18),0_12px_26px_-12px_rgba(197,155,39,0.45)]"
          : "border-sand bg-white hover:border-gold/60 hover:bg-gold-light/40 hover:-translate-y-0.5 hover:shadow-lg"
      }`}
    >
      <span className="text-base sm:text-lg font-semibold text-navy">{label}</span>
      <span className={`w-6 h-6 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${selected ? "border-transparent bg-gradient-to-br from-[#DFB748] to-[#997314]" : "border-sand"}`}>
        {selected && <Check className="w-4 h-4 text-navy" strokeWidth={3} />}
      </span>
    </motion.button>
  );
}

function ProgressHeader({ step, total, progress, label }) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between text-xs font-extrabold uppercase tracking-[0.22em] text-navy mb-2">
        <span>Question {String(step + 1).padStart(2, "0")} of {String(total).padStart(2, "0")}</span>
        <span className="text-gold">{progress}%</span>
      </div>
      <div className="h-2.5 bg-sand/60 rounded-full overflow-hidden scroll-mt-28 md:scroll-mt-40" data-testid="assessment-progress-bar">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-[#A8821C] via-gold to-[#DFB748] gold-shimmer" animate={{ width: `${progress}%` }} transition={{ duration: 0.5, ease: "easeOut" }} />
      </div>
      <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{label}</p>
    </div>
  );
}

export default function QuestionStep({ pathway, question, answers, step, total, progress, isLast, onSelect, onToggle, onBack, onNext }) {
  const isMulti = question.type === "multi";
  const current = answers[question.id];
  const isSelected = (opt) => (isMulti ? (current || []).includes(opt.value) : current === opt.value);

  return (
    <>
      <ProgressHeader step={step} total={total} progress={progress} label={pathway.label} />
      <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy leading-snug">{question.title}</h2>
      {question.subtitle && <p className="mt-2 text-slate-500 font-semibold">{question.subtitle}</p>}
      <div className="mt-7 space-y-3">
        {question.options.map((opt) => (
          <OptionCard key={opt.value} label={opt.label} selected={isSelected(opt)} onClick={() => (isMulti ? onToggle(question, opt) : onSelect(question, opt.value))} />
        ))}
      </div>
      <div className="mt-8 flex items-center justify-between">
        <button onClick={onBack} data-testid="assessment-back-button" className="inline-flex items-center gap-2 min-h-[48px] px-5 rounded-full border border-sand bg-white text-navy font-bold text-sm hover:border-gold transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {isMulti && (
          <button
            onClick={onNext}
            disabled={!(current || []).length}
            data-testid="assessment-next-button"
            className="inline-flex items-center gap-2 min-h-[52px] px-7 rounded-full btn-gold-luxe text-navy font-extrabold text-sm tracking-widest transition-all hover:-translate-y-0.5 disabled:opacity-50"
          >
            {isLast ? "SEE MY RESULTS" : "CONTINUE"} <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </>
  );
}
